import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import SettingsUsers from "../Security/Users";

import {
  getOnboardingCatalogue,
  getOnboardingFirmProfile,
  provisionOnboardingCatalogue,
  saveOnboardingCatalogueDraft,
  updateOnboardingPreference,
  updateOnboardingFirmProfile,
} from "../../helpers/backend_helper";

const steps = [
  ["profile", "Firm Profile"],
  ["team", "Teams"],
  ["inspection", "Finish"],
];
const emptyProfile = {
  name: "",
  primaryContactName: "",
  address: "",
  contactNo: "",
  emailId: "",
  website: "",
  activeUserCount: 0,
};

const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function FirmOnboardingWorkspace({
  progressError,
  onRefresh,
  onComplete,
  onFinish,
}) {
  const [catalogue, setCatalogue] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [stage, setStage] = useState("profile");
  const [standards, setStandards] = useState([]);
  const [reviewTypes, setReviewTypes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const includeInspectionRisks = true;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [tourOpen, setTourOpen] = useState(false);
  const [inspectionChoice, setInspectionChoice] = useState("");

  useEffect(() => {
    setSaveError("");
  }, [
    profile,
    standards,
    reviewTypes,
    industries,
    workTypes,
    templates,
    includeInspectionRisks,
    inspectionChoice,
  ]);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [catalogueResponse, profileResponse] = await Promise.all([
        getOnboardingCatalogue(),
        getOnboardingFirmProfile(),
      ]);
      if (!catalogueResponse?.status || !catalogueResponse.data)
        throw new Error(catalogueResponse?.message || "Catalogue unavailable");
      if (!profileResponse?.status || !profileResponse.data)
        throw new Error(profileResponse?.message || "Firm profile unavailable");
      const value = catalogueResponse.data;
      setCatalogue(value);
      setProfile({ ...emptyProfile, ...profileResponse.data });
      const draft = value.draft || {};
      const postInspection = (value.reviewTypes || []).filter(
        (item) =>
          ["postinspectionreview", "postissuanceinspectionreview"].includes(
            String(item.name || "").replace(/[^a-z]/gi, "").toLowerCase()
          )
      );
      if (!postInspection.length)
        throw new Error(
          "Post-Inspection Review is unavailable. Ask a PIERS System Administrator to publish this review type."
        );
      setStandards(value.standards.map((item) => item.id));
      setReviewTypes(postInspection.map((item) => item.id));
      setIndustries(value.industries.map((item) => item.id));
      setWorkTypes((value.workTypes || []).map((item) => item.id));
      setTemplates(value.templates.map((item) => item.id));
      // Legacy practice/template drafts must pass through profile to apply the defaults.
      const defaultsApplied =
        draft.includeInspectionRisks === true &&
        value.templates.every((item) => item.isProvisioned) &&
        [value.standards, value.industries, value.workTypes || []].every(
          (items) => items.every((item) => item.isProvisioned)
        ) &&
        draft.reviewTypeIds?.length === postInspection.length &&
        postInspection.every((item) => draft.reviewTypeIds.includes(item.id));
      setStage(
        defaultsApplied &&
          ["settings", "team", "engagements"].includes(draft.completedStep)
          ? draft.completedStep === "settings"
            ? "team"
            : "inspection"
          : "profile"
      );
    } catch (error) {
      setLoadError(
        error?.message || "We couldn’t load your onboarding workspace."
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    document
      .querySelector(".firm-onboarding__screen-inner")
      ?.scrollTo?.({ top: 0, behavior: "auto" });
  }, [stage]);

  const requiredChecklistIds = useMemo(
    () => [
      ...new Set(
        (catalogue?.templates || [])
          .filter((item) => templates.includes(item.id))
          .flatMap((item) => item.checklistIds)
      ),
    ],
    [catalogue, templates]
  );
  const index = steps.findIndex(([id]) => id === stage);

  const openStep = (stepIndex) => {
    if (stepIndex >= index) return;
    setSaveError("");
    setTourOpen(false);
    setStage(steps[stepIndex][0]);
  };

  const advanceTo = (stepIndex) => {
    const target = Math.min(stepIndex, steps.length - 1);
    setSaveError("");
    setTourOpen(false);
    setStage(steps[target][0]);
  };

  const goBack = () => openStep(Math.max(index - 1, 0));

  const goNext = () => advanceTo(index + 1);

  const showStepError = (message) => {
    setSaveError(message);
    window.requestAnimationFrame(() =>
      document
        .querySelector(".firm-onboarding__screen-inner")
        ?.scrollTo?.({ top: 0, behavior: "smooth" })
    );
  };

  const saveProfile = async () => {
    const pending = [];
    if (!profile.name.trim()) pending.push("enter the firm name");
    if (!profile.primaryContactName.trim())
      pending.push("enter the primary contact name");
    if (!validEmail(profile.emailId))
      pending.push("enter a valid contact email");
    if (pending.length) {
      showStepError(`Complete this step: ${pending.join(" and ")}.`);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const response = await updateOnboardingFirmProfile(profile);
      if (!response?.status)
        throw new Error(
          response?.message || "Firm profile could not be saved."
        );
      const provisionResponse = await provisionOnboardingCatalogue({
        standardIds: standards,
        reviewTypeIds: reviewTypes,
        industryIds: industries,
        workTypeIds: workTypes,
        templateIds: templates,
        checklistIds: requiredChecklistIds,
      });
      if (!provisionResponse?.status)
        throw new Error(
          provisionResponse?.message || "Catalogue could not be prepared."
        );
      const draftResponse = await saveOnboardingCatalogueDraft({
        standardIds: standards,
        reviewTypeIds: reviewTypes,
        industryIds: industries,
        workTypeIds: workTypes,
        includeInspectionRisks,
        completedStep: "settings",
      });
      if (!draftResponse?.status)
        throw new Error(
          draftResponse?.message ||
            "Your onboarding progress could not be saved."
        );
      setCatalogue((current) => ({ ...current, draft: draftResponse.data }));
      setProfile({ ...emptyProfile, ...response.data });
      await onRefresh();
      advanceTo(1);
    } catch (error) {
      setSaveError(error?.message || "We couldn’t save the firm profile.");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async (completedStep) => {
    setSaving(true);
    setSaveError("");
    try {
      const response = await saveOnboardingCatalogueDraft({
        standardIds: standards,
        reviewTypeIds: reviewTypes,
        industryIds: industries,
        workTypeIds: workTypes,
        includeInspectionRisks,
        completedStep,
      });
      if (!response?.status)
        throw new Error(
          response?.message || "Your onboarding choices could not be saved."
        );
      setCatalogue((current) => ({ ...current, draft: response.data }));
      goNext();
    } catch (error) {
      setSaveError(
        error?.message || "We couldn’t save your choices. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const finishSetup = async () => {
    if (!inspectionChoice) {
      showStepError(
        "Complete this step: choose whether you want to import engagements."
      );
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      // Persist the established completion marker used by existing onboarding consumers.
      const response = await updateOnboardingPreference({
        lastStep: 6,
        tourCompleted: true,
      });
      if (!response?.status)
        throw new Error(
          response?.message || "Your setup completion could not be saved."
        );
      onComplete?.();
      await onRefresh();
      onFinish?.(inspectionChoice);
    } catch (error) {
      setSaveError(
        error?.message || "We couldn’t finish setup. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };
  if (loading)
    return (
      <section className="firm-onboarding__state">
        <span>
          <FeatherIcon icon="loader" />
        </span>
        <h1 className="piers-page-title">Preparing your onboarding</h1>
        <p>We’re loading your firm profile and starter catalogue.</p>
      </section>
    );
  if (loadError)
    return (
      <section className="firm-onboarding__state">
        <span className="is-error">
          <FeatherIcon icon="alert-circle" />
        </span>
        <h1 className="piers-page-title">We couldn’t open onboarding</h1>
        <p>{loadError}</p>
        <button type="button" onClick={load}>
          Try again
        </button>
      </section>
    );

  return (
    <div className="firm-onboarding">
      <section className="firm-onboarding__screen">
        <nav
          className="firm-onboarding__wizard"
          aria-label="Firm onboarding progress"
        >
          <div className="firm-onboarding__wizard-heading">
            <h2>Firm Setup</h2>
            <p>
              Confirm your firm profile, review your team, and finish setup.
            </p>
          </div>
          <ol>
            {steps.map(([id, label], stepIndex) => (
              <li
                key={id}
                data-step={id}
                className={`${id === stage ? "is-current" : ""}${
                  stepIndex < index ? " is-complete" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => openStep(stepIndex)}
                  disabled={saving || stepIndex >= index}
                  aria-current={id === stage ? "step" : undefined}
                  title={
                    stepIndex < index
                      ? `Open completed step: ${label}`
                      : id === stage
                      ? `${label} is the current step`
                      : "Complete the earlier steps first"
                  }
                >
                  <i>
                    {stepIndex < index ? (
                      <FeatherIcon icon="check" />
                    ) : (
                      stepIndex + 1
                    )}
                  </i>
                  <span>{label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <div className="firm-onboarding__screen-inner">
          {progressError && (
            <div className="firm-onboarding__error">{progressError}</div>
          )}
          {stage === "profile" && (
            <ProfileScreen
              profile={profile}
              setProfile={setProfile}
              error={saveError}
            />
          )}
          {stage === "team" && (
            <TeamScreen activeUserCount={profile.activeUserCount} />
          )}
          {stage === "inspection" && (
            <InspectionScreen
              choice={inspectionChoice}
              setChoice={setInspectionChoice}
              error={saveError}
            />
          )}
        </div>
        <footer className="firm-onboarding__controls">
          <button
            type="button"
            className="is-back"
            onClick={goBack}
            disabled={index === 0 || saving}
          >
            <FeatherIcon icon="arrow-left" />
            Back
          </button>
          {stage === "profile" && (
            <button
              type="button"
              className="is-next"
              disabled={saving}
              onClick={saveProfile}
            >
              {saving ? "Saving…" : "Save and continue"}
              <FeatherIcon icon="arrow-right" />
            </button>
          )}
          {stage === "team" && (
            <button
              type="button"
              className="is-next"
              disabled={saving}
              onClick={() => saveDraft("team")}
            >
              {saving ? "Saving…" : "Save and continue"}
              <FeatherIcon icon="arrow-right" />
            </button>
          )}
          {stage === "inspection" && (
            <button
              type="button"
              className="is-next"
              disabled={saving}
              onClick={finishSetup}
            >
              {saving ? "Finishing…" : "Finish"}
              <FeatherIcon icon="check" />
            </button>
          )}
        </footer>
      </section>
      <SupportPanel
        stage={stage}
        index={index}
        tourOpen={tourOpen}
        setTourOpen={setTourOpen}
      />
      {tourOpen && (
        <GuidedTour
          key={stage}
          stage={stage}
          onClose={() => setTourOpen(false)}
        />
      )}
    </div>
  );
}

const guidance = {
  profile: {
    title: "Firm profile",
    body: "Confirm the firm identity and contact details used in inspections and reports.",
    points: [
      "Use the firm’s official name",
      "Identify the primary contact",
      "Provide a monitored contact email",
      "Check the address and contact number",
    ],
    outcome:
      "These details appear throughout PIERS and must be saved before continuing.",
  },
  team: {
    title: "Team administration",
    body: "Review everyone who has access to your firm and manage the team without leaving setup.",
    points: [
      "Search or filter existing users",
      "Review roles, status and user actions",
      "Use Add user to invite a team member",
      "Continue when your team is ready",
    ],
    outcome:
      "Saved changes refresh the user list and active-user count automatically.",
  },
  inspection: {
    title: "Finish setup",
    body: "Your firm setup is complete. Choose whether you want to import engagements. Select Yes to open the engagement import wizard after finishing.",
    points: [
      "Review what is now available",
      "Choose Yes or No",
      "Select Finish to complete onboarding",
    ],
    outcome:
      "You can create inspection cycles, add engagements and run inspection reviews at any time.",
  },
};

const tourSteps = {
  profile: [
    {
      title: "Review your firm profile",
      body: "Confirm the firm name, primary contact, contact email and address PIERS will use across reports and inspections.",
      selector: ".firm-onboarding__profile-form",
    },
    {
      title: "Save before moving on",
      body: "Save and continue records these details and prepares all practice options and templates before Teams.",
      selector: ".firm-onboarding__controls .is-next",
    },
  ],
  team: [
    {
      title: "Find the right user",
      body: "Search by name or email, then narrow the list by role or account status.",
      selector: ".firm-onboarding__team .security-users__toolbar",
    },
    {
      title: "Review your team",
      body: "See each user’s role, email and current status. Open the action menu to manage an existing user.",
      selector: ".firm-onboarding__team .security-users__grid",
    },
    {
      title: "Add a team member",
      body: "Select Add user to open the familiar invitation drawer without leaving onboarding.",
      selector: ".firm-onboarding__team-heading .firm-onboarding__provision",
    },
    {
      title: "Continue when ready",
      body: "When the team looks right, continue to Finish.",
      selector: ".firm-onboarding__controls .is-next",
    },
  ],
  inspection: [
    {
      title: "Choose what happens next",
      body: "Choose Yes to open the engagement import wizard, or No to return to the firm dashboard.",
      selector: ".firm-onboarding__completion-choice",
    },
    {
      title: "Finish setup",
      body: "Finish saves your completed onboarding setup and follows the option you selected.",
      selector: ".firm-onboarding__controls .is-next",
    },
  ],
};

function SupportPanel({ stage, index, tourOpen, setTourOpen }) {
  const item = guidance[stage];
  return (
    <aside
      className="firm-onboarding__rail firm-onboarding__support"
      aria-label="Onboarding help"
    >
      <section className="firm-onboarding__support-card">
        <div className="firm-onboarding__support-kicker">
          <span>
            <FeatherIcon icon="help-circle" />
          </span>
          <small>NEED HELP?</small>
        </div>
        <h2>{item.title}</h2>
        <p>{item.body}</p>
        <div className="firm-onboarding__support-details">
          <strong>What to do</strong>
          <ul>
            {item.points.map((point) => (
              <li key={point}>
                <FeatherIcon icon="check" />
                {point}
              </li>
            ))}
          </ul>
          <em>{item.outcome}</em>
        </div>
        <a href="/help" target="_blank" rel="noreferrer">
          Open Help Center
          <FeatherIcon icon="external-link" />
        </a>
      </section>
      <section className="firm-onboarding__tour">
        <div className="firm-onboarding__support-kicker">
          <span>
            <FeatherIcon icon="compass" />
          </span>
          <small>GUIDED TOUR</small>
        </div>
        <h2>Walk me through this step</h2>
        <p>See the important actions for this screen.</p>
        <button type="button" onClick={() => setTourOpen(true)}>
          <FeatherIcon icon="play" />
          {tourOpen ? "Tour in progress" : "Start guided tour"}
        </button>
      </section>
      <div className="firm-onboarding__support-progress">
        <span>Setup progress</span>
        <strong>
          {index + 1} of {steps.length}
        </strong>
        <i>
          <b style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
        </i>
      </div>
    </aside>
  );
}

function GuidedTour({ stage, onClose }) {
  const items = tourSteps[stage] || [];
  const [step, setStep] = useState(0);
  const [target, setTarget] = useState(null);
  const [rect, setRect] = useState(null);
  const item = items[step];

  useLayoutEffect(() => {
    const element = item?.selector
      ? document.querySelector(item.selector)
      : null;
    setTarget(element);
    element?.scrollIntoView?.({ behavior: "smooth", block: "center" });

    const update = () => setRect(element?.getBoundingClientRect?.() || null);
    const frame = window.requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [item]);
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  if (!item) return null;
  const width = 350;
  const left = Math.max(
    16,
    Math.min(
      window.innerWidth - width - 16,
      (rect?.left || window.innerWidth / 2) + (rect?.width || 0) / 2 - width / 2
    )
  );
  const placeBelow = !rect || rect.bottom + 205 < window.innerHeight;
  const top = rect
    ? placeBelow
      ? rect.bottom + 14
      : Math.max(76, rect.top - 196)
    : 100;
  const finish = step === items.length - 1;
  return (
    <div
      className="firm-onboarding__tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Guided onboarding tour"
    >
      <div className="firm-onboarding__tour-shade" onClick={onClose} />
      {target && rect && (
        <div
          className="firm-onboarding__tour-spotlight"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}
      <section
        className={`firm-onboarding__tour-coach${
          placeBelow ? " is-below" : " is-above"
        }`}
        style={{ top, left, width }}
      >
        <button
          type="button"
          className="firm-onboarding__tour-close"
          onClick={onClose}
          aria-label="Close guided tour"
        >
          <FeatherIcon icon="x" />
        </button>
        <h2>{item.title}</h2>
        <p>{item.body}</p>
        <footer>
          <span>
            {step + 1} of {items.length}
          </span>
          <button type="button" className="is-skip" onClick={onClose}>
            Skip Tour
          </button>
          <div>
            {step > 0 && (
              <button
                type="button"
                className="is-back"
                onClick={() => setStep((value) => value - 1)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="is-next"
              onClick={() =>
                finish ? onClose() : setStep((value) => value + 1)
              }
            >
              {finish ? "Done" : "Next"}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

function ProfileScreen({ profile, setProfile, error }) {
  const field = (name, label, props = {}) => (
    <label className={props.wide ? "is-wide" : ""}>
      <span>
        {label}
        {props.required && <b> *</b>}
      </span>
      {props.textarea ? (
        <textarea
          value={profile[name]}
          onChange={(event) =>
            setProfile({ ...profile, [name]: event.target.value })
          }
          placeholder={props.placeholder}
          rows="4"
        />
      ) : (
        <input
          type={props.type || "text"}
          value={profile[name]}
          onChange={(event) =>
            setProfile({ ...profile, [name]: event.target.value })
          }
          placeholder={props.placeholder}
        />
      )}
    </label>
  );
  return (
    <div>
      <SelectionScreen
        eyebrow="STEP 1 OF 3"
        title="Tell us about your firm"
        description="Confirm the details PIERS will use across inspections, reports and firm communications."
      />
      <div className="firm-onboarding__profile-form">
        {field("name", "Firm name", {
          required: true,
          placeholder: "Enter firm name",
        })}
        {field("primaryContactName", "Primary contact", {
          required: true,
          placeholder: "Enter full name",
        })}
        {field("emailId", "Contact email", {
          required: true,
          type: "email",
          placeholder: "firm@example.com",
        })}
        {field("contactNo", "Contact number", {
          placeholder: "Enter contact number",
        })}
        {field("website", "Website", { placeholder: "https://example.com" })}
        {field("address", "Registered address", {
          wide: true,
          textarea: true,
          placeholder: "Enter the firm address",
        })}
      </div>
      {error && (
        <div className="firm-onboarding__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function TeamScreen({ activeUserCount }) {
  const [createRequest, setCreateRequest] = useState(0);
  const [activeCount, setActiveCount] = useState(activeUserCount);

  const updateCount = (users) =>
    setActiveCount(
      users.filter((user) => user.status === 1 || user.status === true).length
    );
  return (
    <div className="firm-onboarding__team">
      <SelectionScreen
        eyebrow="STEP 2 OF 3"
        title="Review your team"
        description="As Firm Administrator, review access, manage users or invite a team member."
      />
      <div className="firm-onboarding__team-heading">
        <div>
          <strong>
            {activeCount} active user{activeCount === 1 ? "" : "s"}
          </strong>
          <span>
            Invites and changes use the same secure Admin Console workflow.
          </span>
        </div>
        <button
          type="button"
          className="firm-onboarding__provision"
          onClick={() => setCreateRequest((value) => value + 1)}
        >
          <FeatherIcon icon="user-plus" />
          Add user
        </button>
      </div>
      <SettingsUsers
        embedded
        createRequest={createRequest}
        onUsersLoaded={updateCount}
      />
    </div>
  );
}

function InspectionScreen({ choice, setChoice, error }) {
  return (
    <div className="firm-onboarding__focus-screen firm-onboarding__focus-screen--complete">
      <span className="is-finish">
        <FeatherIcon icon="check" />
      </span>
      <SelectionScreen
        eyebrow="STEP 3 OF 3"
        title="Your firm setup is complete"
        description="You can now create inspection cycles, add engagements and run inspection reviews."
      />
      {error && (
        <div className="firm-onboarding__error" role="alert">
          {error}
        </div>
      )}
      <section
        className="firm-onboarding__completion-choice"
        role="radiogroup"
        aria-label="Import engagements"
      >
        <strong>Do you want to import engagements?</strong>
        <div>
          <button
            type="button"
            className={choice === "yes" ? "is-selected" : ""}
            aria-pressed={choice === "yes"}
            onClick={() => setChoice("yes")}
          >
            <FeatherIcon icon="check-circle" />
            <span>
              <b>Yes</b>
              <small>Open the engagement import wizard after finishing</small>
            </span>
          </button>
          <button
            type="button"
            className={choice === "no" ? "is-selected" : ""}
            aria-pressed={choice === "no"}
            onClick={() => setChoice("no")}
          >
            <FeatherIcon icon="clock" />
            <span>
              <b>No</b>
              <small>Finish setup and import engagements later</small>
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}

function SelectionScreen({ eyebrow, title, description }) {
  return (
    <header className="firm-onboarding__question">
      <p>{eyebrow}</p>
      <div className="firm-onboarding__question-copy">
        <h1 className="piers-page-title">{title}</h1>
        <span>{description}</span>
      </div>
    </header>
  );
}
